<script setup lang="ts">
import type { GameWinnerListProps } from '@game/types'
import type { CSSProperties } from 'vue'
import { computed, toRef } from '#imports'

const WINNER_TILE_HEIGHT = 68
const props = withDefaults(defineProps<GameWinnerListProps>(), { tileVariant: 'short', fade: true, horizontal: false })
const isBingo = toRef(() => props.tileVariant === 'bingo')
const styles = computed(() => {
    const styles: CSSProperties = {}

    if (!isBingo.value && !props.horizontal) {
        const minHeight = styles.minHeight ? `${props.minHeight}px` : `${props.winners.length * WINNER_TILE_HEIGHT}px`

        if (props.tileVariant === 'short') {
            styles.height = minHeight
        }
        else {
            styles.minHeight = minHeight
        }
    }
    else if (props.horizontal) {
        styles.height = 'auto'
    }

    return styles
})
</script>

<template>
    <div
        class="winners-wrap"
        :class="{
            'winners-wrap--fade': props.fade !== false,
            'winners--horizontal': horizontal
        }"
    >
        <TransitionGroup
            appear
            tag="ul"
            :name="isBingo ? 'flip-in-bingo' : 'flip-in'"
            class="winners"
            :class="{
                'winners--bingo': isBingo,
                'winners--horizontal': horizontal
            }"
            :style="styles"
        >
            <GameWinnerTile
                v-for="winner in winners"
                :key="winner.id"
                :winner="winner"
                :variant="tileVariant"
                :horizontal="horizontal"
            />
        </TransitionGroup>
    </div>
</template>

<style lang="scss">
.winners {
    padding: 0;
    margin: 0;
    position: relative;
    overflow: hidden;
    perspective: 500px;
    min-height: 235px;

    &-wrap {
        display: block;
        position: relative;
        margin-bottom: 10px;

        @include tablet {
            margin-bottom: 0;
        }

        &--fade {
            -webkit-mask-image: $winners-fade-mask-down;
            mask-image: $winners-fade-mask-down;

            // Horizontal lists scroll sideways, so feather the right edge instead.
            &.winners--horizontal {
                -webkit-mask-image: $winners-fade-mask-right;
                mask-image: $winners-fade-mask-right;
                align-content: center;
            }
        }
    }

    &--bingo {
        display: flex;
        position: relative;
        flex-direction: column;
        z-index: z(bingo-winners);
        width: 100%;
        max-width: max-content;
        perspective: 800px;

        @media (min-width: $screen-md) {
            flex-direction: row;
            perspective-origin: 50%;
            min-height: 52px;
        }
    }

    &--horizontal {
        overflow-x: auto;
        overflow-y: hidden;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;

        ::-webkit-scrollbar {
            display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;

        .winners {
            display: flex;
            position: relative;
            width: 100%;
            flex-direction: row;
            min-height: 50px;
            perspective: 400px;
            max-width: max-content;
        }
    }
}

.flip-in,
.flip-in-bingo {
    &-enter-from {
        transform: rotateX(180deg);
        opacity: 0;
    }

    &-enter-active {
        transition: all 0.7s $easeOutQuart;
    }

    &-enter-to {
        transform: rotateX(0deg);
    }
}

// Animations used outside components
.flip-in {
    &-enter-to {
        @for $i from 1 to 12 {
            &:nth-child(#{$i}) {
                transition-delay: ($i * 0.05s);
            }
        }

        @for $i from 12 to 24 {
            &:nth-child(#{$i}) {
                //.35s added as buffer for leaving elements
                transition-delay: (($i * 0.05s) + 0.35s);
            }
        }
    }

    &-leave-from {
        transform: rotateX(0deg);
    }

    &-leave-active {
        transition: all 0.6s $easeOutQuart;
    }

    &-leave-to {
        transform: rotateX(-180deg);

        @for $i from 1 to 12 {
            &:nth-child(#{$i}) {
                transition-delay: ($i * 0.05s);
            }
        }
    }

    &-move {
        transition: all 0.5s $easeOutQuad;

        &:not(.flip-in-enter-to) {
            transition-delay: 0s !important;
        }
    }
}

.flip-in-bingo {
    &-enter-to {
        @for $i from 1 to 6 {
            &:nth-child(#{$i}) {
                transition-delay: ($i * 0.08s);
            }
        }
    }
}
</style>
